import { createFileRoute, Link } from "@tanstack/react-router";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Badge } from "@/components/ui/badge";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { ScannerApp } from "@/components/scanner/ScannerApp";

const PATH = "/barcode-reader";
const TITLE = "Online Barcode Reader — Scan UPC, EAN, QR & More";
const DESC =
  "Free online barcode reader. Scan with your camera, or upload an image, to decode UPC, EAN, GTIN, QR, Data Matrix and more — all in your browser.";

const FAQ = [
  {
    q: "Which barcode formats does the reader support?",
    a: "The reader decodes UPC-A, UPC-E, EAN-8, EAN-13, Code 39, Code 93, Code 128, ITF (Interleaved 2 of 5), ITF-14, RSS-14, RSS Expanded, QR Code, Data Matrix, Aztec and PDF417.",
  },
  {
    q: "Do You Upload My Images to a Server?",
    a: "No. The camera stream stays in your browser, and uploaded images are decoded locally with JavaScript. Pasted image URLs are loaded directly by your browser too — we don't proxy them.",
  },
  {
    q: "The Camera Scanner Isn't Starting — What Should I Check?",
    a: "Camera access requires HTTPS and a user gesture. Make sure you're on a secure connection, allow camera permission when prompted, and that no other tab is currently using the camera.",
  },
  {
    q: "Why Didn't My Uploaded Image Decode?",
    a: "Try a sharper, well-lit photo where the barcode is straight, fills most of the frame, and isn't reflective or wrinkled. For dense 2D codes (QR, Data Matrix), avoid heavy compression.",
  },
  {
    q: "Can I Paste an Image URL From Another Website?",
    a: "Yes, if the host allows cross-origin image loading (CORS). If a URL fails, download the image and use the upload option instead.",
  },
  {
    q: "Can a Barcode Scanner Scan a Phone Screen?",
    a: "A camera-based scanner can, and that includes this online reader: point the camera at the screen, or upload a screenshot of it. Laser scanners usually cannot, because a screen does not reflect the laser beam the way a paper label does. If glare washes out part of the code, tilt the phone slightly until the bars look solid.",
  },
  {
    q: "How Does a Barcode Reader Work?",
    a: "A barcode reader measures the dark bars and light spaces of a 1D code, or the grid of dark and light modules in a 2D code, and converts them back into characters using the rules of that symbology. This reader does it in software: it turns each camera frame or image into black and white, looks for a known start pattern or finder pattern, and checks the result against the check digit or error correction before showing it.",
  },
  {
    q: "Can Barcode Scanners Read All Barcodes?",
    a: "No. Laser scanners read only 1D barcodes, and every scanner, hardware or software, decodes only the symbologies it was built or configured for. This reader covers the formats listed on this page but not Codabar or GS1 DataBar Limited. It also returns one barcode per scan, so crop an image that holds several codes before uploading it.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Readers", path: "/readers" },
  { name: "Online Barcode Reader", path: PATH },
];

export const Route = createFileRoute("/barcode-reader")({
  component: BarcodeReaderPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Online Barcode Reader",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function BarcodeReaderPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Online Barcode Reader"
      description="Scan a barcode with your camera, upload an image, or paste an image URL. Decoded entirely in your browser. It reads retail UPC and EAN codes, Code 128 and ITF shipping labels, QR codes, Data Matrix, Aztec and PDF417, and retries a tightly cropped image with an added white margin before reporting that no barcode was found."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the online barcode reader"
            about={
              <>
                <p>
                  The ReadBarcode online barcode reader scans 1D and 2D barcodes directly in your
                  browser — straight from your phone camera, a webcam, an image upload or an image URL.
                  Decoding runs locally in JavaScript, so the barcode never leaves your device. It
                  works for retail UPC/EAN codes, logistics labels with Code 128 or ITF, QR codes and
                  Data Matrix.
                </p>
                <p>
                  Use this when you need to scan a barcode online without installing an app, or when you
                  want to verify what a printed label actually encodes.
                </p>
              </>
            }
            whenToUse={[
              "Reading a product barcode on a store shelf from your phone.",
              "Decoding a Code 128 carton label from a quick photo or upload.",
              "Checking a QR code on a poster without opening it on your phone.",
              "Debugging what a generated barcode actually encodes.",
              "Scanning when you don't trust unknown app store scanner apps.",
              "Quick desktop scanning over a webcam for catalog work.",
            ]}
            howItWorks={[
              {
                title: "Pick an input",
                desc: "Choose camera, image upload or paste an image URL — whichever is fastest.",
              },
              {
                title: "Allow access if needed",
                desc: "Grant camera permission once. Uploads and URLs don't need any permission.",
              },
              {
                title: "Frame the barcode",
                desc: "Hold the code flat and steady. The reader auto-detects 1D and 2D formats.",
              },
              {
                title: "Get the decoded value",
                desc: "The result panel shows the value and format.",
              },
              {
                title: "Copy or scan again",
                desc: "Copy the value with one click or hit Scan another to scan the next one.",
              },
            ]}
            whatToAvoid={[
              "Blurry, very dark or very washed-out photos — the decoder needs sharp edges.",
              "Reflections on glossy packaging that wipe out part of the bars.",
              "Cropping that cuts off the quiet zone (white space) around a barcode.",
              "Heavily rotated or warped images — straighten them first if you can.",
              "Expecting a UPC to come back with product info on its own — that needs a lookup tool.",
            ]}
            tips={[
              "Good even lighting beats a high-resolution camera every time.",
              "Move your phone slightly closer instead of zooming in.",
              "If a 1D code fails, try cropping tighter and uploading the cropped image.",
              "For QR codes on screens, dim your monitor a bit to reduce glare.",
              "Damaged labels often still decode if you photograph them straight on.",
            ]}
          />
          <DecodingByType />
          <PrivacyNote />
          <HowToRead />
          <OnlineVsHandheld />
          <BarcodeNumbers />
          <WhyNoRead />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <ScannerApp />
      <SupportedFormats />
    </ToolLayout>
  );
}

function SupportedFormats() {
  const formats = [
    "UPC-A",
    "UPC-E",
    "EAN-8",
    "EAN-13",
    "ITF / ITF-14",
    "Code 39",
    "Code 93",
    "Code 128",
    "QR Code",
    "Data Matrix",
    "Aztec",
    "PDF417",
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Supported Barcode Formats</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        The reader decodes the major 1D and 2D symbologies used across retail, logistics and
        healthcare.
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {formats.map((f) => (
          <Badge key={f} variant="outline" className="px-3 py-1 text-sm">
            {f}
          </Badge>
        ))}
      </div>
    </section>
  );
}

function DecodingByType() {
  const rows = [
    {
      type: "Retail UPC / EAN",
      tip: "Fill about 60% of the frame and keep the bars horizontal. These 1D codes decode fastest when the quiet zones on both ends are visible.",
    },
    {
      type: "Code 128 / ITF-14 cartons",
      tip: "Shoot straight on under even light. Long logistics codes fail when the photo is taken at an angle that compresses the narrow bars.",
    },
    {
      type: "QR codes",
      tip: "Hold steady so all three corner finder squares are sharp. On a screen, dim the display to kill glare before scanning.",
    },
    {
      type: "Data Matrix / GS1 2D",
      tip: "Get close — these are small and dense. Avoid heavy JPEG compression on uploads, which smears the tiny modules together.",
    },
    {
      type: "Damaged or curved labels",
      tip: "Photograph the label flattened and straight on. Partial damage often still decodes, because a scan line can cross an undamaged part of a 1D code and 2D codes carry error correction.",
    },
  ];
  return (
    <>
      <h2>Decoding tips by barcode type</h2>
      <p>
        The reader auto-detects the format, but a little framing know-how gets a clean read on the
        first try.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Barcode type</th>
              <th scope="col">How to get a clean read</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.type}>
                <td>
                  <strong>{r.type}</strong>
                </td>
                <td>{r.tip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function PrivacyNote() {
  return (
    <>
      <h2>Private by Default</h2>
      <p>
        Decoding runs in your browser using a JavaScript port of the ZXing library. Camera
        frames and uploaded images never leave your device, and we don't store the barcodes you
        scan.
      </p>
    </>
  );
}

function HowToRead() {
  return (
    <>
      <h2>How to Read a Barcode</h2>
      <p>To read a barcode, pick Camera, Image or URL in the tool above and aim it at the code; the decoded value appears in the Result panel.</p>
      <ol>
        <li>Press Start camera, drop an image onto the Image tab, or paste a link and press Decode URL.</li>
        <li>Check the format badge and the value. The camera stops after the first successful read.</li>
        <li>Press Copy value, or Scan another to clear the panel for the next code.</li>
      </ol>
      <p>Since the value is plain text, the page doubles as a barcode to text reader: paste it into a spreadsheet or order form instead of retyping digits.</p>
      <h3>Scan a Barcode With Your Phone</h3>
      <p>Open this page in your phone browser, tap Start camera and allow camera access. The reader asks for the rear camera at up to 1080p and requests continuous autofocus where the phone supports it. Nothing is installed, so the barcode reader online free works on a work phone where you cannot add a free barcode scanner app.</p>
      <h3>Scan a Barcode Online Free With a Webcam</h3>
      <p>To scan a barcode online free on a laptop or desktop, use the same Camera tab with a built-in or USB webcam, which makes this a barcode reader for computer use with no software to install. If the webcam cannot focus that close, photograph the code with your phone and upload the photo instead.</p>
    </>
  );
}

function OnlineVsHandheld() {
  const rows = [
    {
      point: "Cost",
      online: "Free, with the camera you own",
      handheld: "A device to buy",
    },
    {
      point: "Many items in a row",
      online: "One code per scan",
      handheld: "Built for fast repeated scanning",
    },
    {
      point: "2D codes such as QR",
      online: "Yes",
      handheld: "Imager models only; laser models read 1D only",
    },
    {
      point: "Where the value goes",
      online: "Shown on the page to copy",
      handheld: "Typed into the field that has the cursor",
    },
    {
      point: "Image files and links",
      online: "Yes",
      handheld: "No",
    },
  ];
  return (
    <>
      <h2>Online Barcode Scanner vs a Handheld Barcode Scanner</h2>
      <p>An online barcode scanner suits occasional reads and checking what a code contains, while a handheld barcode scanner is faster for scanning dozens of items into another program.</p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">This online reader</th>
              <th scope="col">Handheld scanner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.point}>
                <th scope="row">{r.point}</th>
                <td>{r.online}</td>
                <td>{r.handheld}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function BarcodeNumbers() {
  return (
    <>
      <h2>How to Read a Barcode Number</h2>
      <p>The number printed under the bars is the same value a scan returns. On retail packs it is a GTIN, whose structure shows who licensed it and whether it was copied correctly, but not what the product is.</p>
      <p>Product names and prices live in brand and retailer databases, so finding them takes a lookup, not a better scan. To check what kind of number you have, paste it into the <Link to="/barcode-type-detector">barcode type detector</Link>.</p>
      <h3>What the Digits Under a Barcode Mean</h3>
      <ul>
        <li>A UPC-A shows 12 digits and an EAN-13 shows 13. A UPC-E shows 8 and is a zero-suppressed UPC-A.</li>
        <li>The first two or three digits of an EAN-13 are a GS1 prefix naming the GS1 member organization that licensed the number, not the country of origin.</li>
        <li>An EAN-13 starting with 978, or with 979 followed by a digit other than 0, carries a book&apos;s ISBN.</li>
        <li>An EAN-13 or UPC-A starting with 2 is a restricted circulation number assigned for internal use, often on variable-weight items, and means nothing outside that store or country.</li>
        <li>The last digit is a check digit calculated from the others with the GS1 modulo-10 method, so one mistyped digit makes the check fail.</li>
      </ul>
      <p>The guide on <Link to="/barcode-guides/what-is-a-gtin">what a GTIN is and how it is issued</Link> covers how these numbers are allocated.</p>
    </>
  );
}

function WhyNoRead() {
  return (
    <>
      <h2>Why a Barcode Cannot Be Read</h2>
      <p>A barcode cannot be read when the image loses the contrast between bars and spaces, when its quiet zone is missing, or when the reader does not support its symbology.</p>
      <ul>
        <li>Motion blur or poor focus smears narrow bars into wide ones.</li>
        <li>Glare from shrink film or lamination wipes out part of the symbol.</li>
        <li>Light bars on a dark background are inverted, and this reader looks for dark bars on light, so it may miss them.</li>
        <li>Text touching the bars hides the edges of a 1D code. For uploads and image links, the reader retries with a white margin added.</li>
        <li>1D codes have no error correction, while QR Code, Data Matrix, Aztec and PDF417 use Reed-Solomon error correction and tolerate some damage.</li>
        <li>Codabar and GS1 DataBar Limited are not decoded here.</li>
      </ul>
      <p>To identify an unfamiliar symbology, see the <Link to="/barcode-guides/barcode-formats">overview of barcode formats and where each is used</Link>.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/read-barcode-from-image",
      label: "Read barcode from image",
      desc: "Drag-and-drop or upload a photo of a barcode.",
    },
    {
      path: "/qr-code-reader",
      label: "QR code reader",
      desc: "Decode QR codes and parse links, Wi-Fi, contacts.",
    },
    {
      path: "/data-matrix-reader",
      label: "Data Matrix reader",
      desc: "Decode Data Matrix codes from healthcare, manufacturing, and logistics labels.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN validator",
      desc: "Verify GTIN-8/12/13/14 check digits.",
    },
    {
      path: "/gs1-digital-link-decoder",
      label: "GS1 Digital Link decoder",
      desc: "Parse GS1 URLs into GTIN, lot, serial and expiry.",
    },
    {
      path: "/barcode-type-detector",
      label: "Barcode type detector",
      desc: "Identify whether a number is a UPC, EAN, GTIN-14 or ISBN.",
    },
    {
      path: "/barcode-guides/barcode-formats",
      label: "Barcode formats guide",
      desc: "Compare the 1D and 2D symbologies and where each is used.",
    },
    {
      path: "/barcode-guides/what-is-a-gtin",
      label: "What is a GTIN?",
      desc: "How the number under a retail barcode is structured and issued.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-lg font-semibold">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-medium group-hover:text-primary">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
